import { gql, useMutation } from '@apollo/client'
import _ from 'lodash'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'

import { INavParams } from 'pared/Routes/navParams'
import { FE2_BASE_URL } from 'pared/constants'
import { getBrandFromPathname } from 'pared/utils/brand'
import {
  getFe2CustomerCode,
  recordFe2SessionSeed,
  signInUser,
} from 'pared/utils/user'

import Main from './Main'

const MUTATION_AUTHENTICATE_USER = gql`
  mutation AuthenticateUser($email: String!, $password: String!) {
    authenticate(input: { iEmail: $email, iPassword: $password }) {
      jwtToken
    }
  }
`

interface IProps {
  navParams: INavParams
}

function SignIn({ navParams }: IProps) {
  const pagePathname = _.get(navParams, 'pagePathname', '')
  const brand = getBrandFromPathname(pagePathname)
  const location = useLocation<{ signInMagicLinkError?: string }>()
  const [errorMessage, setErrorMessage] = useState<string>(
    location.state?.signInMagicLinkError || '',
  )
  const [authenticateUser, { loading, error }] = useMutation(
    MUTATION_AUTHENTICATE_USER,
  )

  const onSignIn = async (email: string, password: string) => {
    const formattedEmail = (email || '').trim().toLowerCase()

    if (!formattedEmail) {
      setErrorMessage('The email is required.')
      return false
    }

    if (!password) {
      setErrorMessage('The password is required.')
      return false
    }

    try {
      const fe2CustomerCode = getFe2CustomerCode(brand)
      // why: transport failures (FE2.0 down, CORS refused) must not block V1
      // sign-in - a null response is observed below instead of bubbling into
      // the catch, whose message would blame the user's credentials.
      const fe2Response = await fetch(
        `${FE2_BASE_URL}/api/${fe2CustomerCode}/iframe-auth/sign-in`,
        {
          method: 'POST',
          // why: the endpoint sets a cross-origin session cookie; without
          // 'include' the browser silently drops the Set-Cookie response header.
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formattedEmail,
            password,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
        },
      ).catch(() => null)

      // why: a failed seed must be recorded, not silent - syncFe2Session
      // skips its forced V1 sign-out when the seed is known to have failed,
      // which is what would otherwise turn "FE2.0 rejected this user" into a
      // sign-out loop on every app load.
      recordFe2SessionSeed(fe2CustomerCode, Boolean(fe2Response?.ok))
      if (!fe2Response?.ok) {
        console.error('FE2.0 session seed failed:', fe2Response?.status)
      }

      const response = await authenticateUser({
        variables: {
          password,
          email: formattedEmail,
        },
      })

      if (response.data?.authenticate?.jwtToken) {
        await signInUser(response.data.authenticate.jwtToken, formattedEmail)
        return true
      } else {
        setErrorMessage('The email or the password may be incorrect.')
        return false
      }
    } catch (error: any) {
      setErrorMessage(error || 'The email or the password may be incorrect.')
      return false
    }
  }

  return (
    <Main
      isLoading={loading}
      onSignIn={onSignIn}
      errorMessage={error?.message || errorMessage}
      brand={brand}
    />
  )
}

export default SignIn
